/** biome-ignore-all lint/suspicious/noConsole: allow console in logger */
import type { ConsoleMethod } from '@mntn-dev/edge-config'
import type { AnyRecord } from '@mntn-dev/utility-types'

import type { LoggerFunction, LoggerInstance, LogLevel } from './log-types.ts'

const shouldLog = () =>
  process.env.NODE_ENV === 'test'
    ? process.env.SILENT === 'false'
    : process.env.SILENT !== 'true'

// Color utilities for local development
const colors = {
  reset: '\x1b[0m',
  bright: '\x1b[1m',
  red: '\x1b[31m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  blue: '\x1b[34m',
  magenta: '\x1b[35m',
  cyan: '\x1b[36m',
  white: '\x1b[37m',
  gray: '\x1b[90m',
  brightRed: '\x1b[91m',
  brightGreen: '\x1b[92m',
  brightYellow: '\x1b[93m',
  brightBlue: '\x1b[94m',
  brightMagenta: '\x1b[95m',
  brightCyan: '\x1b[96m',
} as const

// Predefined colors for different namespaces
const namespaceColors = [
  colors.cyan,
  colors.magenta,
  colors.green,
  colors.yellow,
  colors.blue,
  colors.brightCyan,
  colors.brightMagenta,
  colors.brightGreen,
  colors.brightYellow,
  colors.brightBlue,
  colors.red,
  colors.brightRed,
] as const

// Check if colors should be enabled (local development only)
const shouldUseColors = async (): Promise<boolean> => {
  // Check if we're in a browser environment
  const isBrowser = typeof window !== 'undefined'

  if (isBrowser) {
    // In browser, check local storage configuration
    const config = await getCachedConfig()
    return config.colorsEnabled && process.env.NODE_ENV !== 'production'
  }

  // In Node.js, use the original logic
  return (
    process.env.NODE_ENV !== 'production' &&
    process.env.NODE_ENV !== 'test' &&
    !process.env.CI &&
    !process.env.VERCEL &&
    typeof process !== 'undefined' &&
    process.stdout?.isTTY
  )
}

// Get a consistent color for a namespace
const getNamespaceColor = async (namespace: string): Promise<string> => {
  if (!(await shouldUseColors()) && namespace) {
    return ''
  }

  // Simple hash function to get consistent color for namespace
  let hash = 0
  for (let i = 0; i < namespace.length; i++) {
    const char = namespace.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash &= hash // Convert to 32-bit integer
  }

  const colorIndex = Math.abs(hash) % namespaceColors.length
  return namespaceColors[colorIndex] || ''
}

// Level-specific colors
const levelColors = {
  debug: colors.gray,
  info: colors.blue,
  warn: colors.yellow,
  error: colors.red,
} as const

// Create colored prefix for namespace
const createColoredPrefix = async (namespace?: string): Promise<string> => {
  if (!namespace) {
    return 'magicsky '
  }

  const namespaceColor = await getNamespaceColor(namespace)
  const reset = (await shouldUseColors()) ? colors.reset : ''

  if (await shouldUseColors()) {
    // Use namespace color for the namespace only
    return `${namespaceColor}magicsky:${namespace}${reset} `
  }

  return `magicsky:${namespace} `
}

type LoggingConfig = {
  debugFilter: string[]
  consoleMethods: string[]
  colorsEnabled: boolean
}

// Valid console methods for validation
const validConsoleMethods = ['debug', 'info', 'warn', 'error'] as const
const isValidConsoleMethod = (
  method: string
): method is (typeof validConsoleMethods)[number] =>
  validConsoleMethods.includes(method as (typeof validConsoleMethods)[number])

// Global cache state
let cachedConfig: LoggingConfig | null = null
let cachePromise: Promise<LoggingConfig> | null = null

// Function to reset the configuration cache
export const resetLoggerConfig = () => {
  cachedConfig = null
  cachePromise = null
}

const hasEdgeConfigConnectionString = () =>
  typeof process !== 'undefined' &&
  !!process.env &&
  typeof process.env.EDGE_CONFIG === 'string' &&
  process.env.EDGE_CONFIG.length > 0

// Get logging configuration from the appropriate source
const getLoggingConfig = async (): Promise<LoggingConfig> => {
  const isBrowser = typeof window !== 'undefined'

  if (isBrowser) {
    return getBrowserConfig()
  }

  if (hasEdgeConfigConnectionString()) {
    try {
      const { edgeConfig } = await import('@mntn-dev/edge-config')
      const { debugFilter, consoleMethods } = await edgeConfig()

      return {
        debugFilter:
          debugFilter && typeof debugFilter === 'string' ? [debugFilter] : [],
        consoleMethods:
          consoleMethods && Array.isArray(consoleMethods)
            ? consoleMethods.filter(
                (m): m is ConsoleMethod =>
                  typeof m === 'string' && isValidConsoleMethod(m)
              )
            : ['debug', 'info', 'warn', 'error'],
        colorsEnabled: true,
      }
    } catch (error) {
      console.warn(
        'Failed to get logging config from Edge Config, falling back to environment variables:',
        error
      )
      return getLocalLoggingConfig()
    }
  }

  return getLocalLoggingConfig()
}

// Extract local logging config logic for reuse
const getLocalLoggingConfig = (): LoggingConfig => {
  try {
    return {
      debugFilter: process.env.DEBUG ? [process.env.DEBUG] : [],
      consoleMethods: process.env.CONSOLE_METHODS
        ? process.env.CONSOLE_METHODS.split(',')
            .map((m) => m.trim())
            .filter((m) => m && isValidConsoleMethod(m))
        : ['debug', 'info', 'warn', 'error'],
      colorsEnabled: true,
    }
  } catch (error) {
    console.warn('Failed to parse CONSOLE_METHODS, using defaults:', error)
    return {
      debugFilter: process.env.DEBUG ? [process.env.DEBUG] : [],
      consoleMethods: ['debug', 'info', 'warn', 'error'],
      colorsEnabled: true,
    }
  }
}

const getCachedConfig = async (): Promise<LoggingConfig> => {
  if (cachedConfig !== null) {
    return cachedConfig
  }

  if (cachePromise === null) {
    cachePromise = getLoggingConfig()
  }

  cachedConfig = await cachePromise
  return cachedConfig
}

// Parse DEBUG environment variable for namespace filtering
const getDebugNamespaces = (debugFilters: string[]) => {
  if (debugFilters.length === 0) {
    return { inclusions: new Set<string>(), exclusions: new Set<string>() }
  }

  const inclusions = new Set<string>()
  const exclusions = new Set<string>()

  for (const namespace of debugFilters.flatMap((filter) =>
    filter.split(',').map((namespace) => namespace.trim())
  )) {
    if (namespace.startsWith('-')) {
      // Exclusion pattern (remove the '-' prefix)
      exclusions.add(namespace.slice(1))
    } else {
      // Inclusion pattern
      inclusions.add(namespace)
    }
  }

  return { inclusions, exclusions }
}

const isDebugEnabled = (
  debugNamespaces: { inclusions: Set<string>; exclusions: Set<string> },
  namespace?: string
) => {
  // If no DEBUG env var is set, debug is disabled
  if (
    debugNamespaces.inclusions.size === 0 &&
    debugNamespaces.exclusions.size === 0
  ) {
    return false
  }

  const fullNamespace = namespace ? `magicsky:${namespace}` : 'magicsky'

  // Check exclusions first - if namespace is explicitly excluded, return false
  for (const exclusionPattern of debugNamespaces.exclusions) {
    // Check exact exclusion match
    if (exclusionPattern === fullNamespace) {
      return false
    }

    // Check wildcard exclusion matches (e.g., -magicsky:user-service*)
    if (
      exclusionPattern.endsWith('*') &&
      fullNamespace.startsWith(exclusionPattern.slice(0, -1))
    ) {
      return false
    }
  }

  // If there are inclusions, check if namespace matches any inclusion pattern
  if (debugNamespaces.inclusions.size > 0) {
    // If DEBUG=*, enable all debug logs (unless excluded above)
    if (debugNamespaces.inclusions.has('*')) {
      return true
    }

    // Check exact inclusion match
    if (debugNamespaces.inclusions.has(fullNamespace)) {
      return true
    }

    // Check wildcard inclusion matches (e.g., magicsky:user-service*)
    for (const inclusionPattern of debugNamespaces.inclusions) {
      if (
        inclusionPattern.endsWith('*') &&
        fullNamespace.startsWith(inclusionPattern.slice(0, -1))
      ) {
        return true
      }
    }

    // If there are inclusions but no match found, return false
    return false
  }

  // If there are only exclusions and namespace wasn't excluded, enable it
  return true
}

const isConsoleMethodEnabled = (consoleMethods: string[], method: string) => {
  return consoleMethods.includes(method)
}

const useStructuredLogging = () =>
  typeof process !== 'undefined' &&
  !!process.env &&
  (process.env.VERCEL === '1' || !!process.env.VERCEL_URL)

const structuredReplacer = (_key: string, value: unknown): unknown => {
  if (value instanceof Error) {
    const plain: Record<string, unknown> = {
      name: value.name,
      message: value.message,
      stack: value.stack,
    }
    if (value.cause !== undefined) {
      plain.cause = value.cause
    }
    return plain
  }
  if (typeof value === 'bigint') {
    return value.toString()
  }
  return value
}

const emitStructuredLog = (
  level: LogLevel,
  namespace: string | undefined,
  message: string,
  args: AnyRecord<string> | undefined
): void => {
  const fullNamespace = namespace ? `magicsky:${namespace}` : 'magicsky'
  try {
    const entry: Record<string, unknown> = {
      ...args,
      message,
      level,
      namespace: fullNamespace,
    }
    shouldLog() && console[level](JSON.stringify(entry, structuredReplacer))
  } catch {
    shouldLog() &&
      console[level](
        JSON.stringify({
          message,
          level,
          namespace: fullNamespace,
          serializationError: true,
        })
      )
  }
}

const createDebugLogger = (namespace?: string): LoggerFunction => {
  /**
   * The debug logger is used to log debug messages to the console
   * We use console.debug directly to ensure datadog can capture these logs
   * But we maintain the DEBUG environment variable filtering functionality
   */
  return async (message, args) => {
    const config = await getCachedConfig()

    // Check if console.debug method is enabled
    if (!isConsoleMethodEnabled(config.consoleMethods, 'debug')) {
      return
    }

    // Check if debug logging is enabled for this namespace
    const debugNamespaces = getDebugNamespaces(config.debugFilter)
    if (!isDebugEnabled(debugNamespaces, namespace)) {
      return
    }

    // if the args object has a property named `message`
    // rename it to `messageText` so it is not lost in datadog
    const argsObject = args?.message
      ? { ...args, messageText: args.message }
      : args

    if (useStructuredLogging()) {
      emitStructuredLog('debug', namespace, message, argsObject)
      return
    }

    const prefix = await createColoredPrefix(namespace)
    const levelColor = (await shouldUseColors()) ? levelColors.debug : ''
    const reset = (await shouldUseColors()) ? colors.reset : ''
    shouldLog() &&
      console.debug(
        `${prefix}${levelColor}[debug]${reset} ${message}`,
        ...(argsObject !== undefined ? [argsObject] : [])
      )
  }
}

const createLevelLogger = (
  level: LogLevel,
  namespace?: string
): LoggerFunction => {
  return async (message, args) => {
    const config = await getCachedConfig()

    // Check if this console method is enabled
    if (!isConsoleMethodEnabled(config.consoleMethods, level)) {
      return
    }

    // if the args object has a property named `message`
    // rename it to `messageText` so it is not lost in datadog
    const argsObject = args?.message
      ? { ...args, messageText: args.message }
      : args

    if (useStructuredLogging()) {
      emitStructuredLog(level, namespace, message, argsObject)
      return
    }

    const prefix = await createColoredPrefix(namespace)
    const levelColor = (await shouldUseColors()) ? levelColors[level] : ''
    const reset = (await shouldUseColors()) ? colors.reset : ''
    shouldLog() &&
      console[level](
        `${prefix}${levelColor}[${level}]${reset} ${message}`,
        ...(argsObject !== undefined ? [argsObject] : [])
      )
  }
}

/**
 * This function is used to create a repository logger instance
 * @param namespace the namespace used by the debug logger
 * @returns a repository logger instance
 */
export const Logger = (namespace?: string): LoggerInstance => ({
  info: createLevelLogger('info', namespace),
  warn: createLevelLogger('warn', namespace),
  error: createLevelLogger('error', namespace),
  debug: createDebugLogger(namespace),
  log: (level: LogLevel) => createLevelLogger(level, namespace),
})

export const logger = Logger()

// Test function to verify debug filtering (for development/testing purposes)
export const testDebugFilter = (
  debugFilter: string,
  namespace: string,
  expected: boolean
) => {
  const { inclusions, exclusions } = getDebugNamespaces([debugFilter])
  const result = isDebugEnabled({ inclusions, exclusions }, namespace)
  const passed = result === expected

  console.log(
    `Test: DEBUG=${debugFilter}, namespace=${namespace}, expected=${expected}, got=${result}, ${passed ? 'PASS' : 'FAIL'}`
  )

  return passed
}

// Browser local storage keys
const BROWSER_CONFIG_KEYS = {
  DEBUG: 'magicsky:logger:debug',
  CONSOLE_METHODS: 'magicsky:logger:consoleMethods',
  COLORS: 'magicsky:logger:colors',
} as const

// Get browser configuration from local storage
const getBrowserConfig = (): LoggingConfig => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return {
        debugFilter: [],
        consoleMethods: ['debug', 'info', 'warn', 'error'],
        colorsEnabled: true,
      }
    }

    const debugFilter = window.localStorage.getItem(BROWSER_CONFIG_KEYS.DEBUG)
    const consoleMethods = window.localStorage.getItem(
      BROWSER_CONFIG_KEYS.CONSOLE_METHODS
    )
    const colorsEnabled = window.localStorage.getItem(
      BROWSER_CONFIG_KEYS.COLORS
    )

    return {
      debugFilter: debugFilter ? [debugFilter] : [],
      consoleMethods: consoleMethods
        ? consoleMethods
            .split(',')
            .map((m) => m.trim())
            .filter((m) => m && isValidConsoleMethod(m))
        : ['debug', 'info', 'warn', 'error'],
      colorsEnabled: colorsEnabled === 'true',
    }
  } catch (_error) {
    // Fallback to defaults if local storage fails
    return {
      debugFilter: [],
      consoleMethods: ['debug', 'info', 'warn', 'error'],
      colorsEnabled: true,
    }
  }
}

// Set browser configuration in local storage
export const setBrowserLoggerConfig = (config: {
  debug?: string
  consoleMethods?: string
  colors?: boolean
}) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return
    }

    if (config.debug !== undefined) {
      window.localStorage.setItem(BROWSER_CONFIG_KEYS.DEBUG, config.debug)
    }
    if (config.consoleMethods !== undefined) {
      window.localStorage.setItem(
        BROWSER_CONFIG_KEYS.CONSOLE_METHODS,
        config.consoleMethods
      )
    }
    if (config.colors !== undefined) {
      window.localStorage.setItem(
        BROWSER_CONFIG_KEYS.COLORS,
        config.colors.toString()
      )
    }

    // Reset the cache so new config is picked up
    resetLoggerConfig()
  } catch (error) {
    console.warn('Failed to set browser logger config:', error)
  }
}

// Get current browser configuration
export const getBrowserLoggerConfig = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null
    }

    return {
      debug: window.localStorage.getItem(BROWSER_CONFIG_KEYS.DEBUG),
      consoleMethods: window.localStorage.getItem(
        BROWSER_CONFIG_KEYS.CONSOLE_METHODS
      ),
      colors: window.localStorage.getItem(BROWSER_CONFIG_KEYS.COLORS),
    }
  } catch (_error) {
    return null
  }
}

// Clear browser configuration
export const clearBrowserLoggerConfig = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return
    }

    window.localStorage.removeItem(BROWSER_CONFIG_KEYS.DEBUG)
    window.localStorage.removeItem(BROWSER_CONFIG_KEYS.CONSOLE_METHODS)
    window.localStorage.removeItem(BROWSER_CONFIG_KEYS.COLORS)

    // Reset the cache so new config is picked up
    resetLoggerConfig()
  } catch (error) {
    console.warn('Failed to clear browser logger config:', error)
  }
}
